import React from 'react';
import { connect } from 'react-redux';

import { UserAccountId } from 'api/UserAccount/model/UserAccountId';
import { ISignUpFormState } from 'shared/components/SignUpOrSignIn/SignUpForm/reducers';

import { Button } from 'shared/components/Button';
import { ConnectedEmbeddedSignUpForm, IConnectedEmbeddedSignUpFormProps } from 'shared/components/SignUpOrSignIn/EmbeddedSignUpForm/EmbeddedSignUpForm';
import { IEmbeddedSignUpFormState } from 'shared/components/SignUpOrSignIn/EmbeddedSignUpForm/reducers';
import { SignUpOptionsFormActions } from 'shared/components/SignUpOrSignIn/SignUpOptions/actions';
import { ISignUpAndLoginButtonsState } from '../SignUpAndLoginButtons/reducers';
import { DownloadGateActions, DownloadGateActionInterfaces } from './actions';

import { DjangoApiManager } from 'shared/api/DjangoApiManager';
import { RuntimeException } from 'shared/lib/general/exceptions/RuntimeException';
import { PathUtils } from 'shared/utils/pathUtils';
import { BevSpotDispatch } from 'shared/components/Provider';

import 'shared/css/MakeItSpin.scss';

const DOWNLOAD_GATE_MOUNT_ELEMENT_ID = 'download-gate-mount'; // Note: This should match the ID of the mount element for this component because we've decided to set the needed data attribute there
const DOWNLOAD_URL_ATTRIBUTE_NAME = 'data-download-url';
const DOWNLOAD_BUTTON_TEXT_ATTRIBUTE_NAME = 'data-download-button-text';
const ADDITIONAL_FIELDS_ARE_SHOWN_ATTRIBUTE_NAME = 'data-show-additional-fields';

const djangoApiManager = new DjangoApiManager();

interface IDownloadGateProps {
    signUpAndLoginButtonsState : ISignUpAndLoginButtonsState;
    embeddedSignUpFormState : IEmbeddedSignUpFormState;
}

interface IConnectedDownloadGateProps extends IDownloadGateProps {
    dispatch : BevSpotDispatch<IDownloadGateProps, { services: DownloadGateActionInterfaces.IServices }>;
}

class DownloadGate extends React.Component<IConnectedDownloadGateProps, object> {

    private downloadUrl : string | null = null;
    private downloadButtonText : string = 'Download';
    private additionalFieldsAreShown : boolean = false;

    public UNSAFE_componentWillMount() {
        const downloadGateMountElement : HTMLElement | null = document.getElementById(DOWNLOAD_GATE_MOUNT_ELEMENT_ID);

        if (downloadGateMountElement) {
            const downloadUrlAttributeValue = downloadGateMountElement.getAttribute(DOWNLOAD_URL_ATTRIBUTE_NAME);
            downloadGateMountElement.removeAttribute(DOWNLOAD_URL_ATTRIBUTE_NAME);

            if (!downloadUrlAttributeValue) {
                djangoApiManager.logJsError(
                    'Could not get download url data attribute. Found element with Id: ' + DOWNLOAD_GATE_MOUNT_ELEMENT_ID + 'Was expecting attribute: ' + DOWNLOAD_URL_ATTRIBUTE_NAME,
                    null,
                    new RuntimeException('Could not get download url data attribute'),
                    true
                );
            }

            this.downloadUrl = downloadUrlAttributeValue;

            const downloadButtonTextAttributeValue = downloadGateMountElement.getAttribute(DOWNLOAD_BUTTON_TEXT_ATTRIBUTE_NAME);
            if (downloadButtonTextAttributeValue) {
                this.downloadButtonText = downloadButtonTextAttributeValue;
            }

            const additionalFieldsAreShownAttributeValue = downloadGateMountElement.getAttribute(ADDITIONAL_FIELDS_ARE_SHOWN_ATTRIBUTE_NAME);
            if (additionalFieldsAreShownAttributeValue) {
                this.additionalFieldsAreShown = additionalFieldsAreShownAttributeValue === 'true';
            }
        } else {
            djangoApiManager.logJsError(
                'Could not find mount point for download gate when trying to get download url. Was expecting ' + DOWNLOAD_GATE_MOUNT_ELEMENT_ID,
                null,
                new RuntimeException('Could not find mount point for download gate'),
                true
            );
            this.downloadUrl = null;
        }
    }

    public render() {
        const {
            dispatch,
            signUpAndLoginButtonsState,
            embeddedSignUpFormState,
        } = this.props;

        if (typeof(signUpAndLoginButtonsState.loggedInUser) === 'undefined') {
            return (
                <div>
                    <span className="bevicon bevico-spinner2 make-it-spin loading-spinner"/>
                </div>
            );
        }

        let downloadElement : JSX.Element | null = null;
        if (this.downloadUrl) {
            const onClick = () => {
                if (this.downloadUrl) {
                    window.open(this.downloadUrl, '_blank');
                } else {
                    window.open(PathUtils.getAbsolutePathForRequest('/user'), '_blank');

                    djangoApiManager.logJsError(
                        'Value of download url somehow changed!',
                        null,
                        new RuntimeException('Value of download url somehow changed!'),
                        true);
                }

                dispatch(DownloadGateActions.onDownloadClicked(this.downloadUrl));
            };

            downloadElement = (
                <div>
                    <Button
                        buttonClassName="bs-psb"
                        isLoading={ false }
                        isDisabled={ false }
                        onClick={ onClick }
                    >
                        { this.downloadButtonText }
                    </Button>
                </div>
            );
        } else {
            const onClick = () => {
                window.open(PathUtils.getAbsolutePathForRequest('/user'), '_blank');
                dispatch(DownloadGateActions.onDownloadClicked(this.downloadUrl));
            };

            downloadElement = (
                <div>
                    <p>Go to your user dashboard to get access.</p>
                    <Button
                        buttonClassName="bs-psb"
                        isLoading={ false }
                        isDisabled={ false }
                        onClick={ onClick }
                    >
                        Go to user dashboard
                    </Button>
                </div>
            );
        }

        if ((signUpAndLoginButtonsState.loggedInUser === null)) {
            let aboveForm : JSX.Element | null = null;
            let belowForm : JSX.Element | null = null;

            if (embeddedSignUpFormState.signUpForm.hasSuccessfullySubmitted) {
                aboveForm = (
                    <div>
                        <h5>You&rsquo;ve unlocked this resource!</h5>
                        { downloadElement }
                    </div>
                );
            } else {
                const onWantsToLogIn = () => {
                    dispatch(SignUpOptionsFormActions.onSetActiveForm('signInForm'));
                    dispatch(SignUpOptionsFormActions.onSetFormIsShown(true));
                };

                aboveForm  = (
                    <div>
                        <h5>To access this resource, sign up for BevSpot free!</h5>
                    </div>
                );
                belowForm = (
                    <p className="small">
                        &nbsp; <br/>Already have a BevSpot account?<br/>
                        <a onClick={ onWantsToLogIn }>Log in</a> to get access.
                    </p>
                );
            }

            const connectedEmbeddedSignUpFormProps : IConnectedEmbeddedSignUpFormProps = {
                additionalFieldsAreShown : this.additionalFieldsAreShown,
                additionalRetailerFieldsAreRequired : false,
                onSuccessfulSignUp : (userAccountIdentifier : UserAccountId, signUpFormState : ISignUpFormState) => {
                    return dispatch(DownloadGateActions.onSuccessfulSignUp(userAccountIdentifier, signUpFormState));
                },
            };

            return (
                <div className="download-gate">
                    { aboveForm }
                    <ConnectedEmbeddedSignUpForm { ...connectedEmbeddedSignUpFormProps }/>
                    { belowForm }
                </div>
            );
        }

        return (
            <div className="download-gate">
                <p>
                    Welcome back { (signUpAndLoginButtonsState.loggedInUser.userInfo === null) ? '' : ', ' + signUpAndLoginButtonsState.loggedInUser.userInfo.firstName }!
                </p>

                { downloadElement }

                <p>
                    &nbsp; <br/>Remember, by joining you&rsquo;ve unlocked more resources! Check them out on your <a href={ PathUtils.getAbsolutePathForRequest('/user') }>user dashboard</a>
                </p>
            </div>
        );
    }
}

const mapStateToProps = (state : IDownloadGateProps) : IDownloadGateProps => {
    return {
        signUpAndLoginButtonsState : state.signUpAndLoginButtonsState,
        embeddedSignUpFormState : state.embeddedSignUpFormState,
    };
};

export const ConnectedDownloadGate = connect<IDownloadGateProps, object, object, IDownloadGateProps>(mapStateToProps)(DownloadGate);
